import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Loader2, UserPlus, Home, MessageSquare, KeyRound, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Modal, Field, inputCls } from "./common";

export default function CustomersManager() {
  const [customers, setCustomers] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [detail, setDetail] = useState(null); // customer
  const [empModal, setEmpModal] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, e] = await Promise.all([api.get("/users?role=customer"), api.get("/users?role=employee")]);
      setCustomers(c.data); setEmployees(e.data);
    } catch { setCustomers([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const createEmployee = async () => {
    try {
      const { data } = await api.post("/users/employee", empModal);
      toast.success(`Employee created. Temp password: ${data.temp_password}`, { duration: 10000 });
      setEmpModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const resetPw = async (userId) => {
    try { const { data } = await api.post(`/users/${userId}/reset-password`, {}); toast.success(`New temp password: ${data.temp_password}`, { duration: 12000 }); }
    catch (e) { toast.error("Failed"); }
  };
  const removeEmployee = async (userId) => {
    try { await api.delete(`/users/${userId}`); toast.success("Employee removed"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div data-testid="customers-manager">
      {/* Employees */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl font-600 text-charcoal">Team & Customers</h2>
        <button onClick={() => setEmpModal({ name: "", email: "", password: "" })} data-testid="new-employee-btn" className="inline-flex items-center gap-2 bg-charcoal text-white px-4 py-2.5 rounded-sm text-sm font-600 hover:bg-charcoal/90"><UserPlus className="w-4 h-4" /> Add Employee</button>
      </div>
      <div className="bg-white border border-border rounded-sm p-5 mb-8">
        <h3 className="font-600 text-charcoal mb-3">Employees ({employees.length})</h3>
        {employees.length === 0 ? <p className="text-olive text-sm" data-testid="employees-empty">No employees yet.</p> : (
          <div className="space-y-2">
            {employees.map((e) => (
              <div key={e.id} className="flex items-center justify-between py-1.5 border-b border-border last:border-0" data-testid={`employee-${e.id}`}>
                <span className="text-charcoal">{e.name || e.email} <span className="text-olive text-sm">· {e.email}</span></span>
                <div className="flex items-center gap-3">
                  <button onClick={() => resetPw(e.id)} data-testid={`emp-reset-${e.id}`} className="inline-flex items-center gap-1 text-sm text-emerald hover:underline"><KeyRound className="w-3.5 h-3.5" /> Reset PW</button>
                  <button onClick={() => removeEmployee(e.id)} data-testid={`emp-delete-${e.id}`} className="text-red-600 hover:bg-red-50 rounded-sm p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <h3 className="font-600 text-charcoal mb-3">Customers ({customers?.length || 0})</h3>
      {customers === null ? <Loader2 className="w-5 h-5 animate-spin text-olive" /> : customers.length === 0 ? (
        <p className="text-olive" data-testid="customers-empty">No registered customers yet.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {customers.map((c) => (
            <button key={c.id} onClick={() => setDetail(c)} data-testid={`customer-${c.id}`} className="text-left bg-white border border-border rounded-sm p-4 hover:shadow-md transition-shadow">
              <p className="font-600 text-charcoal">{c.name || "Customer"}</p>
              <p className="text-olive text-sm">{c.email}</p>
            </button>
          ))}
        </div>
      )}

      {empModal && (
        <Modal title="Add Employee" onClose={() => setEmpModal(null)} testid="employee-modal">
          <Field label="Name"><input className={inputCls} data-testid="employee-name" value={empModal.name} onChange={(e) => setEmpModal({ ...empModal, name: e.target.value })} /></Field>
          <Field label="Email"><input className={inputCls} data-testid="employee-email" value={empModal.email} onChange={(e) => setEmpModal({ ...empModal, email: e.target.value })} /></Field>
          <Field label="Temp Password (optional — auto-generated if blank)"><input className={inputCls} data-testid="employee-password" value={empModal.password} onChange={(e) => setEmpModal({ ...empModal, password: e.target.value })} /></Field>
          <button onClick={createEmployee} data-testid="employee-save-btn" className="bg-emerald text-white px-5 py-2.5 rounded-sm font-600 hover:bg-emerald-dark">Create Employee</button>
        </Modal>
      )}

      {detail && <CustomerDetail customer={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function CustomerDetail({ customer, onClose }) {
  const cid = customer.id;
  const [props, setProps] = useState([]);
  const [comms, setComms] = useState([]);
  const [propModal, setPropModal] = useState(null);
  const [note, setNote] = useState({ type: "call", subject: "", body: "" });

  const load = useCallback(async () => {
    const [p, c] = await Promise.all([
      api.get(`/properties?customer_id=${cid}`).catch(() => ({ data: [] })),
      api.get(`/communications/${cid}`).catch(() => ({ data: [] })),
    ]);
    setProps(p.data); setComms(c.data);
  }, [cid]);
  useEffect(() => { load(); }, [load]);

  const saveProp = async () => {
    try { await api.post("/properties", { ...propModal, customer_id: cid }); toast.success("Property saved"); setPropModal(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const addNote = async () => {
    if (!note.subject && !note.body) return;
    try { await api.post("/communications", { ...note, customer_id: cid }); setNote({ type: "call", subject: "", body: "" }); load(); }
    catch (e) { toast.error("Failed"); }
  };

  return (
    <Modal title={customer.name || customer.email} onClose={onClose} testid="customer-detail">
      {/* Properties */}
      <div className="flex items-center justify-between">
        <h4 className="font-600 text-charcoal flex items-center gap-1.5"><Home className="w-4 h-4 text-emerald" /> Properties</h4>
        <button onClick={() => setPropModal({ label: "Home", address: "", city: "", gate_code: "", access_notes: "", pet_notes: "", grass_type: "", irrigation_notes: "", fertilizer_history: "", special_instructions: "" })} data-testid="add-property-btn" className="text-sm text-emerald inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      {props.length === 0 ? <p className="text-olive text-sm">No properties.</p> : props.map((p) => (
        <div key={p.id} className="border border-border rounded-sm p-3 text-sm" data-testid={`property-${p.id}`}>
          <p className="font-600 text-charcoal">{p.label} — {p.address}, {p.city}</p>
          {p.gate_code && <p className="text-olive">Gate: {p.gate_code}</p>}
          {p.pet_notes && <p className="text-olive">Pets: {p.pet_notes}</p>}
          {p.grass_type && <p className="text-olive">Grass: {p.grass_type}</p>}
        </div>
      ))}

      {/* Communication log */}
      <h4 className="font-600 text-charcoal flex items-center gap-1.5 pt-2"><MessageSquare className="w-4 h-4 text-emerald" /> Communication Log</h4>
      <div className="flex gap-2">
        <select className="border border-border rounded-sm px-2 text-sm" value={note.type} onChange={(e) => setNote({ ...note, type: e.target.value })} data-testid="comm-type">
          {["call", "email", "in_person", "status_change"].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input className={inputCls} placeholder="Note…" data-testid="comm-body" value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value, subject: e.target.value.slice(0, 40) })} />
        <button onClick={addNote} data-testid="comm-add-btn" className="bg-emerald text-white px-4 rounded-sm font-600">Log</button>
      </div>
      <div className="max-h-48 overflow-y-auto space-y-1.5">
        {comms.map((c) => (
          <div key={c.id} className="text-sm border-l-2 border-emerald/40 pl-2" data-testid={`comm-${c.id}`}>
            <span className="text-xs uppercase text-gold font-600">{c.type}</span> · <span className="text-charcoal">{c.subject || c.body}</span>
            <span className="text-xs text-olive block">{(c.created_at || "").slice(0, 16).replace("T", " ")}</span>
          </div>
        ))}
      </div>

      {propModal && (
        <Modal title="Add Property" onClose={() => setPropModal(null)} testid="property-modal">
          <Field label="Label"><input className={inputCls} value={propModal.label} onChange={(e) => setPropModal({ ...propModal, label: e.target.value })} /></Field>
          <Field label="Address"><input className={inputCls} data-testid="property-address" value={propModal.address} onChange={(e) => setPropModal({ ...propModal, address: e.target.value })} /></Field>
          <Field label="City"><input className={inputCls} value={propModal.city} onChange={(e) => setPropModal({ ...propModal, city: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Gate Code"><input className={inputCls} value={propModal.gate_code} onChange={(e) => setPropModal({ ...propModal, gate_code: e.target.value })} /></Field>
            <Field label="Grass Type"><input className={inputCls} value={propModal.grass_type} onChange={(e) => setPropModal({ ...propModal, grass_type: e.target.value })} /></Field>
          </div>
          <Field label="Pet Notes"><input className={inputCls} value={propModal.pet_notes} onChange={(e) => setPropModal({ ...propModal, pet_notes: e.target.value })} /></Field>
          <Field label="Access / Special Instructions"><textarea rows={2} className={inputCls} value={propModal.special_instructions} onChange={(e) => setPropModal({ ...propModal, special_instructions: e.target.value })} /></Field>
          <button onClick={saveProp} data-testid="property-save-btn" className="bg-emerald text-white px-5 py-2.5 rounded-sm font-600 hover:bg-emerald-dark">Save Property</button>
        </Modal>
      )}
    </Modal>
  );
}
